import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import './BootstrapDatePicker.css';

const BootstrapDatePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select a date", 
  maxDate = null,
  minDate = null,
  disabled = false,
  error = null,
  className = "",
  name = "",
  id = "",
  yearRange = "age-restricted" // "age-restricted" or "unrestricted"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  // Parse initial date value
  const parseInitialDate = (dateValue) => {
    if (!dateValue) return null;
    
    // Handle DD/MM/YYYY format
    if (dateValue.includes('/')) {
      const parts = dateValue.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    }
    
    // Handle YYYY-MM-DD format (fallback)
    return new Date(dateValue);
  };

  const [selectedDate, setSelectedDate] = useState(value ? parseInitialDate(value) : null);
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  
  const datePickerRef = useRef(null);
  const inputRef = useRef(null);

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    const options = { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Format date for input value
  const formatDateForInput = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`; // Returns DD/MM/YYYY format
  };

  // Get days in month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get first day of month
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Check if date is disabled
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (maxDate && date > new Date(maxDate)) return true;
    if (minDate && date < new Date(minDate)) return true;
    if (date > today) return true;
    
    return false;
  };

  // Check if date is selected
  const isDateSelected = (date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    if (isDateDisabled(date)) return;
    
    setSelectedDate(date);
    setIsOpen(false);
    
    // Call onChange with formatted date
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: formatDateForInput(date)
        }
      });
    }
  };

  // Handle month navigation
  const handleMonthChange = (direction) => {
    const newMonth = new Date(currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  // Handle year change
  const handleYearChange = (event) => {
    const newMonth = new Date(currentMonth);
    newMonth.setFullYear(parseInt(event.target.value));
    setCurrentMonth(newMonth);
  };

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Parse date from DD/MM/YYYY format
  const parseDate = (dateString) => {
    if (!dateString) return null;
    
    // Handle DD/MM/YYYY format
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    }
    
    // Handle YYYY-MM-DD format (fallback)
    return new Date(dateString);
  };

  // Update selected date when value prop changes
  useEffect(() => {
    if (value) {
      const parsedDate = parseDate(value);
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        setSelectedDate(parsedDate);
      } else {
        setSelectedDate(null);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isDisabled = isDateDisabled(date);
      const isSelected = isDateSelected(date);
      const isHovered = hoveredDate && hoveredDate.toDateString() === date.toDateString();

      days.push(
        <div
          key={day}
          className={`calendar-day ${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
          onClick={() => handleDateSelect(date)}
          onMouseEnter={() => setHoveredDate(date)}
          onMouseLeave={() => setHoveredDate(null)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  // Get month name by index
  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  // Get month options for dropdown
  const getMonthOptions = () => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months.map((month, index) => (
      <option key={index} value={index}>
        {month}
      </option>
    ));
  };

  // Get year options for dropdown (supports different year ranges)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    
    if (yearRange === "age-restricted") {
      // Generate years for age range 22-55 years
      const minYear = currentYear - 55; // 55 years ago
      const maxYear = currentYear - 22; // 22 years ago
      
      for (let year = minYear; year <= maxYear; year++) {
        years.push(year);
      }
    } else {
      // Generate years for working since (unrestricted - from 50 years ago to current year)
      const minYear = currentYear - 50; // 50 years ago
      const maxYear = currentYear; // Current year
      
      for (let year = minYear; year <= maxYear; year++) {
        years.push(year);
      }
    }
    
    return years.reverse().map(year => (
      <option key={year} value={year}>
        {year}
      </option>
    ));
  };

  // Determine CSS classes based on className prop
  const isProfessionalDatepicker = className.includes('professional-datepicker');
  const containerClass = isProfessionalDatepicker ? 'floating-input-group' : 'emi-homepage-input-field-container';
  const fieldClass = isProfessionalDatepicker ? 'floating-input' : 'emi-homepage-input-field-text';
  const errorClass = isProfessionalDatepicker ? 'error' : 'emi-homepage-input-field-error';

  return (
    <div className={`bootstrap-datepicker ${className}`} ref={datePickerRef}>
      <div className={containerClass}>
        <div className={isProfessionalDatepicker ? '' : `emi-homepage-input-field ${error ? errorClass : ''}`}>
          <input
            ref={inputRef}
            type="text"
            className={fieldClass}
            id={id}
            name={name}
            value={selectedDate ? formatDateForInput(selectedDate) : ''}
            placeholder={isProfessionalDatepicker ? ' ' : placeholder}
            readOnly
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            autoComplete="off"
            spellCheck="false"
            autoCorrect="off"
            autoCapitalize="off"
            data-form-type="other"
          />
          {isProfessionalDatepicker && (
            <label htmlFor={id} className="floating-label">{placeholder}</label>
          )}
          <div className="datepicker-icon" onClick={() => !disabled && setIsOpen(!isOpen)}>
            <i className="fas fa-calendar-alt"></i>
          </div>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="datepicker-dropdown">
          <div className="datepicker-header">
            <div className="datepicker-title">
              <div className="datepicker-selected-date">
                {selectedDate ? formatDate(selectedDate) : 'SELECT DATE'}
              </div>
            </div>
          </div>

          <div className="datepicker-navigation">
            <button
              type="button"
              className="datepicker-nav-btn"
              onClick={() => handleMonthChange('prev')}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            
            <div className="datepicker-month-year">
              <div className="custom-dropdown">
                <button
                  type="button"
                  className="custom-dropdown-button"
                  onClick={() => {
                    setShowMonthDropdown(!showMonthDropdown);
                    setShowYearDropdown(false);
                  }}
                >
                  {getMonthName(currentMonth.getMonth())}
                  <i className="fas fa-chevron-down"></i>
                </button>
                {showMonthDropdown && (
                  <div className="custom-dropdown-menu">
                    <div className="custom-dropdown-scroll">
                      {getMonthOptions().map((option, index) => (
                        <div
                          key={index}
                          className={`custom-dropdown-item ${currentMonth.getMonth() === index ? 'selected' : ''}`}
                          onClick={() => {
                            const newMonth = new Date(currentMonth);
                            newMonth.setMonth(index);
                            setCurrentMonth(newMonth);
                            setShowMonthDropdown(false);
                          }}
                        >
                          {option.props.children}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="datepicker-year-select">
              <div className="custom-dropdown">
                <button
                  type="button"
                  className="custom-dropdown-button"
                  onClick={() => {
                    setShowYearDropdown(!showYearDropdown);
                    setShowMonthDropdown(false);
                  }}
                >
                  {currentMonth.getFullYear()}
                  <i className="fas fa-chevron-down"></i>
                </button>
                {showYearDropdown && (
                  <div className="custom-dropdown-menu">
                    <div className="custom-dropdown-scroll">
                      {getYearOptions().map((option, index) => (
                        <div
                          key={index}
                          className={`custom-dropdown-item ${currentMonth.getFullYear() === parseInt(option.props.value) ? 'selected' : ''}`}
                          onClick={() => {
                            const newMonth = new Date(currentMonth);
                            newMonth.setFullYear(parseInt(option.props.value));
                            setCurrentMonth(newMonth);
                            setShowYearDropdown(false);
                          }}
                        >
                          {option.props.children}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              type="button"
              className="datepicker-nav-btn"
              onClick={() => handleMonthChange('next')}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>

          <div className="datepicker-calendar">
            <div className="calendar-header">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                <div key={day} className="calendar-day-header">{day}</div>
              ))}
            </div>
            <div className="calendar-body">
              {generateCalendarDays()}
            </div>
          </div>

          <div className="datepicker-actions">
            <button
              type="button"
              className="datepicker-action-btn clear-btn"
              onClick={() => {
                setSelectedDate(null);
                setIsOpen(false);
                if (onChange) {
                  onChange({
                    target: {
                      name: name,
                      value: ''
                    }
                  });
                }
              }}
            >
              CLEAR
            </button>
            <button
              type="button"
              className="datepicker-action-btn cancel-btn"
              onClick={() => setIsOpen(false)}
            >
              CANCEL
            </button>
            <button
              type="button"
              className="datepicker-action-btn ok-btn"
              onClick={() => setIsOpen(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="emi-homepage-validation-error">{error}</div>
      )}
    </div>
  );
};

export default BootstrapDatePicker;
